import { z } from "zod";
import { defineStepConfig } from "../../registry";
import { SystemEvent } from "@erp/shared-types";

/**
 * Wait for Event Step
 *
 * A control-flow step that pauses workflow execution until a matching
 * system event arrives. When the event fires, the step completes with
 * the event payload as output and the workflow resumes.
 *
 * Uses the same event selection model as event_trigger, including
 * special-case support for EMAIL_ROUTED_EMAIL_RULE with emailRuleOptionId.
 *
 * Example workflow:
 * ```
 * Create Draft Invoice → Wait for Event (AP_INVOICE_PENDING_APPROVAL) → Auto-approve
 * ```
 */

const EVENTS_WITH_EXTRA_CONFIG = [SystemEvent.EMAIL_ROUTED_EMAIL_RULE] as const;

const FALLBACK_EVENT_TYPES = Object.values(SystemEvent).filter(
  (
    eventType,
  ): eventType is Exclude<
    SystemEvent,
    (typeof EVENTS_WITH_EXTRA_CONFIG)[number]
  > =>
    !EVENTS_WITH_EXTRA_CONFIG.includes(
      eventType as (typeof EVENTS_WITH_EXTRA_CONFIG)[number],
    ),
);

const fallbackEventTypeSchema = z.enum(
  FALLBACK_EVENT_TYPES as [string, ...string[]],
  {
    error: () =>
      "Event type must be one of the following: " +
      FALLBACK_EVENT_TYPES.join(", "),
  },
);

const configSchema = z.discriminatedUnion("eventType", [
  z.object({
    eventType: z.literal(SystemEvent.EMAIL_ROUTED_EMAIL_RULE),
    emailRuleOptionId: z.uuid(),
  }),
  // all event types without other config
  z.object({
    eventType: fallbackEventTypeSchema,
  }),
]);

export type WaitForEventConfig = z.infer<typeof configSchema>;

// Input schema - optional pass-through (step can accept flow control input)
const inputSchema = z.object({}).passthrough();

// Output schema - contains the event data
// Dynamic output sockets are generated from SystemEventMap at save time (same as event_trigger)
const outputSchema = z.object({
  eventData: z.any(),
});

// No emissions from wait steps
const emits = [] as const;

export const waitForEventStep = defineStepConfig({
  type: "wait_for_event",
  name: "Wait for Event",
  description:
    "Pause execution until a matching system event arrives, then resume with event data",
  category: "Control Flow",
  inputSchema,
  outputSchema,
  configSchema,
  emits,
});
